/**
 * SummaryStep.constants.js
 *
 * Screen 12 doesn't collect new fields — it reviews everything captured
 * across Screens 2-11 and lets the user jump back to any section to edit
 * it. SECTION_CONFIGS is the single source of truth for what shows up
 * here and which step "Edit" sends the user back to.
 *
 * Sections with a `formatter` know their data shape exactly (built
 * alongside the step that collects it) and render nicely labeled rows.
 * Sections without one fall back to auto-formatting in SummaryStep.js
 * (prettifies whatever keys/values are present) — swap in a dedicated
 * formatter here once that step's real field shape is confirmed, without
 * touching SummaryStep.js itself.
 */

import { STEP_NUMBERS } from "../Assesment/assessmentConfig";

// Mirrors GoalStep.js's local GOAL_OPTIONS. If GoalStep ever exports
// GOAL_OPTIONS directly, swap this hardcoded map for that import — kept
// separate for now since GoalStep.js keeps it as a private const.
const GOAL_LABELS = {
  lose_weight: "Lose Weight",
  gain_weight: "Gain Weight",
  maintain_weight: "Maintain Weight",
  muscle_gain: "Muscle Gain",
  improve_fitness: "Improve Fitness",
  better_digestion: "Better Digestion",
  healthy_lifestyle: "Healthy Lifestyle",
  pregnancy_nutrition: "Pregnancy Nutrition",
};

const WORK_TYPE_LABELS = { desk: "Desk", standing: "Standing", active: "Active" };
const STRESS_LABELS = { 1: "Very low", 2: "Low", 3: "Moderate", 4: "High", 5: "Very high" };
const YES_NO_LABELS = { yes: "Yes", no: "No" };
const NUM_MEALS_LABELS = {
  3: "3 meals a day",
  4: "4 meals a day",
  5: "5 meals a day",
  6: "6 meals a day",
};
const FASTING_WINDOW_LABELS = {
  "12_12": "12:12",
  "14_10": "14:10",
  "16_8": "16:8",
  "18_6": "18:6",
  "20_4": "20:4",
};
const TRIMESTER_LABELS = { first: "First trimester", second: "Second trimester", third: "Third trimester" };
const CONDITION_LABELS = {
  pregnant: "Pregnant",
  breastfeeding: "Breastfeeding",
  pcos: "PCOS",
  menopause: "Menopause",
  none: "None",
};

const capitalize = (str) =>
  typeof str === "string" && str.length ? str.charAt(0).toUpperCase() + str.slice(1) : str;

export const formatGoal = (goalId) => GOAL_LABELS[goalId] || goalId || "—";

export const formatBasicInfo = (data) => [
  { label: "Name", value: data.name || "—" },
  { label: "Age", value: data.age ? `${data.age} yrs` : "—" },
  { label: "Gender", value: data.gender ? capitalize(data.gender) : "—" },
  // mobileE164 is the canonical value BasicInfoStep hands off (e.g.
  // "+919876543210") — already includes the country's dial code, so it
  // doesn't need mobileCountryCode/mobileDialCode stitched back in here.
  { label: "Mobile number", value: data.mobileE164 || "—" },
  { label: "Email", value: data.email || "Not provided" },
  { label: "Height", value: data.heightCm ? `${data.heightCm} cm` : "—" },
  { label: "Weight", value: data.weightKg ? `${data.weightKg} kg` : "—" },
  { label: "Target weight", value: data.targetWeightKg ? `${data.targetWeightKg} kg` : "Not set" },
];

export const formatLifestyle = (data) => [
  {
    label: "Occupation",
    value: data.occupation ? capitalize(data.occupation.replace(/_/g, " ")) : "Not specified",
  },
  { label: "Work type", value: WORK_TYPE_LABELS[data.workType] || "—" },
  { label: "Wake-up / Sleep", value: `${data.wakeTime || "—"} / ${data.sleepTime || "—"}` },
  { label: "Water intake", value: data.waterIntake >= 5.5 ? "5+ L" : `${data.waterIntake ?? "—"} L` },
  { label: "Stress level", value: STRESS_LABELS[data.stressLevel] || "—" },
  { label: "Smoking", value: YES_NO_LABELS[data.smoking] || "—" },
  { label: "Alcohol", value: YES_NO_LABELS[data.alcohol] || "—" },
];

export const formatMealSchedule = (data) => [
  { label: "Breakfast", value: data.breakfast || "—" },
  { label: "Lunch", value: data.lunch || "—" },
  { label: "Dinner", value: data.dinner || "—" },
  { label: "Bedtime", value: data.bedtime || "Not set" },
  { label: "Number of meals", value: NUM_MEALS_LABELS[data.numMeals] || "No preference" },
  { label: "Fasting window", value: FASTING_WINDOW_LABELS[data.fastingWindow] || "None" },
];

export const formatWomensHealth = (data) => {
  const conditions = data.conditions || [];
  const rows = [
    { label: "Conditions", value: conditions.map((c) => CONDITION_LABELS[c] || c).join(", ") || "—" },
  ];
  if (conditions.includes("pregnant")) {
    rows.push(
      { label: "Trimester", value: TRIMESTER_LABELS[data.trimester] || "—" },
      { label: "Due date", value: data.dueDate || "Not set" },
      { label: "Doctor-advised restrictions", value: data.doctorAdvisedRestrictions || "None noted" },
      { label: "Gestational diabetes", value: YES_NO_LABELS[data.gestationalDiabetes] || "—" }
    );
  }
  return rows;
};

// ── Section registry ─────────────────────────────────────────────────────
// `dataKey` matches the prop name SummaryStep receives for that section.
// Sections without `formatter` fall back to SummaryStep.js's
// auto-formatter. `femaleOnly` sections are omitted entirely when
// `isFemale` is false, matching FSD Section 5's Conditional Display Logic
// for Screen 11.
export const SECTION_CONFIGS = [
  {
    key: "basicInfo",
    title: "Basic Information",
    stepNumber: STEP_NUMBERS.BASIC_INFO,
    dataKey: "basicInfo",
    formatter: formatBasicInfo,
  },
  {
    key: "goal",
    title: "Goal",
    stepNumber: STEP_NUMBERS.GOAL,
    dataKey: "goal",
    formatter: (goalId) => [{ label: "Goal", value: formatGoal(goalId) }],
  },
  {
    key: "lifestyle",
    title: "Lifestyle",
    stepNumber: STEP_NUMBERS.LIFESTYLE,
    dataKey: "lifestyle",
    formatter: formatLifestyle,
  },
  // Auto-formatted — ActivityLevelStep's exact field shape not confirmed yet.
  { key: "activity", title: "Activity Level", stepNumber: STEP_NUMBERS.ACTIVITY, dataKey: "activity" },
  // Auto-formatted — MedicalInfoStep's exact field shape not confirmed yet.
  { key: "medical", title: "Medical Information", stepNumber: STEP_NUMBERS.MEDICAL, dataKey: "medical" },
  // Auto-formatted — FoodPreferencesStep's exact field shape not confirmed yet.
  {
    key: "foodPreferences",
    title: "Food Preferences",
    stepNumber: STEP_NUMBERS.FOOD_PREFERENCES,
    dataKey: "foodPreferences",
  },
  // Auto-formatted, optional per FSD.
  { key: "likes", title: "Likes", stepNumber: STEP_NUMBERS.LIKES, dataKey: "likes" },
  // Auto-formatted, optional per FSD.
  { key: "dislikes", title: "Dislikes", stepNumber: STEP_NUMBERS.DISLIKES, dataKey: "dislikes" },
  {
    key: "mealSchedule",
    title: "Meal Schedule",
    stepNumber: STEP_NUMBERS.MEAL_SCHEDULE,
    dataKey: "mealSchedule",
    formatter: formatMealSchedule,
  },
  {
    key: "womensHealth",
    title: "Women's Health",
    stepNumber: STEP_NUMBERS.WOMENS_HEALTH,
    dataKey: "womensHealth",
    formatter: formatWomensHealth,
    femaleOnly: true,
  },
];